import { GATEWAY_API_URL } from '@admin-panel/config/env';
import { MetaQuery } from '@refinedev/core';
import { axiosInstance } from '../axios';
import CrudApiClient from '../crudapiclient';

export type CollectionStyleUploadRow = {
  rowNumber?: number;
  lkStyleNumber: string;
  rank?: number | null;
};

/**
 * Bulk-adds styles to a manual collection from a CSV upload.
 *
 * SCM answers with collection-level totals plus a list of failed style numbers. ImportWidget
 * needs one result per uploaded row, so this client fans that answer back out over the input —
 * matching failures by style number and treating everything else as applied.
 */
class CollectionStyleUploadApiClient extends CrudApiClient {
  constructor() {
    super(GATEWAY_API_URL + '/catalog/collection');
  }

  async search(): Promise<any> {
    throw new Error('Unimplemented method');
  }

  async create(): Promise<any> {
    throw new Error('Unimplemented method');
  }

  async updateById(): Promise<any> {
    throw new Error('Unimplemented method');
  }

  async findById(): Promise<any> {
    throw new Error('Unimplemented method');
  }

  // @ts-expect-error — ImportWidget drives uploads through createMany
  async createMany(payload: CollectionStyleUploadRow[], meta: MetaQuery | undefined): Promise<any> {
    const collectionId = (meta as any)?.collectionId;
    if (collectionId === undefined || collectionId === null || collectionId === '') {
      throw new Error('collectionId is required to upload collection styles');
    }

    const rows = (payload ?? []).filter(row => Boolean(row?.lkStyleNumber));
    const response = await axiosInstance.post(
      `${this.baseURL}/${collectionId}/products/bulk-add-by-style`,
      {
        styles: rows.map(row => ({
          lkStyleNumber: row.lkStyleNumber,
          rank: row.rank ?? null,
        })),
      },
      {
        headers: {
          'x-lk-orgid': 'lyskraft',
          'x-lk-portalcode': 'pos',
          'x-lk-scid': '1',
          'Content-Type': 'application/json',
        },
      },
    );

    const result = response.data?.data ?? response.data ?? {};
    return toPerRowResults(rows, result?.errors ?? []);
  }
}

type OperationError = { lkStyleNumber?: string; errorMessage?: string; errorCode?: string };

/**
 * Exported for tests: the fan-out from a collection-level response back to per-row results.
 * Matching is case-insensitive because SCM echoes the style number as it was submitted, while
 * merchandiser sheets vary in casing.
 */
export const toPerRowResults = (rows: CollectionStyleUploadRow[], errors: OperationError[]) => {
  const errorByStyle = new Map<string, OperationError>();
  (errors ?? []).forEach(error => {
    if (error?.lkStyleNumber) {
      errorByStyle.set(error.lkStyleNumber.trim().toUpperCase(), error);
    }
  });

  return rows.map(row => {
    const failure = errorByStyle.get(String(row.lkStyleNumber).trim().toUpperCase());
    return {
      input: {
        'LK Style Number': row.lkStyleNumber,
        Rank: row.rank ?? '',
      },
      summary: {
        operation: failure ? 'FAILED' : 'CREATED',
        updatedFields: [],
      },
      remarks: failure
        ? [failure.errorMessage ?? failure.errorCode ?? 'Failed to add style']
        : [],
    };
  });
};

export default CollectionStyleUploadApiClient;
